// Decorative research geometry shared by the gallery masthead and the studies.
// Coordinates describe the visual composition, not a model's hidden states.
const line = (points, extra = {}) => ({ points, phase: -1, ...extra });

function rectangle(x, y, w, h, z) {
    return [[x, y, z], [x + w, y, z], [x + w, y + h, z], [x, y + h, z], [x, y, z]];
}

function box(x, y, w, h, z, thickness, extra = {}) {
    const front = rectangle(x, y, w, h, z);
    const back = rectangle(x, y, w, h, z + thickness);
    return [line(front, extra), line(back, extra), ...front.slice(0, 4).map((p, i) => line([p, back[i]], extra))];
}

// A spatial overview of components discussed in Krea's training report.
// The reward loop is explicit; this is not the original linear figure.
export const trainingLabels = [
    { text: 'DATA', point: [-375, -172, 5] },
    { text: 'PRETRAIN', point: [-95, -172, -30] },
    { text: 'SFT', point: [240, -172, 10] },
    { text: 'PROMPTS', point: [-375, 28, 35] },
    { text: 'DiT', point: [-90, 28, -65] },
    { text: 'SAMPLES', point: [240, 28, 20] },
    { text: 'RL', point: [-90, 200, -10] },
    { text: 'REWARDS', point: [235, 200, 45] },
];

export function trainingGeometry() {
    const curves = [];
    for (const { point: [x, y, z] } of trainingLabels) {
        const w = 205, h = 104, left = x - w / 2, top = y - 40;
        curves.push(...box(left, top, w, h, z, 44));
        for (const d of [14, 28]) curves.push(line(rectangle(left, top, w, h, z + d)));
        // Leave a clear title band; dense cells form the lower portion.
        for (let col = 0; col <= 9; col++) {
            const xx = left + 14 + col * 19.5;
            curves.push(line([[xx, y + 12, z], [xx, top + h - 10, z]]));
        }
        for (let row = 0; row <= 3; row++) {
            const yy = y + 12 + row * 14;
            curves.push(line([[left + 14, yy, z], [left + w - 15, yy, z]]));
        }
    }
    const routes = [
        [[-270,-175,5],[-198,-175,-30]],
        [[8,-175,-30],[137,-175,10]],
        [[240,-108,10],[240,-80,10],[-90,-80,-65],[-90,-12,-65]],
        [[-270,30,35],[-198,30,-65]],
        [[13,30,-65],[137,30,20]],
        [[240,92,20],[240,160,45]],
        [[132,205,45],[13,205,-10]],
        [[-195,205,-10],[-240,205,-10],[-240,90,-65],[-90,90,-65]],
    ];
    routes.forEach((route, index) => {
        // A bundle of parallel wires makes the data paths feel substantial.
        for (let lane = -2; lane <= 2; lane++) curves.push(line(route.map(([x,y,z]) => [x,y+lane*5,z+lane*3])));
        curves.push(...box(-5,-5,10,10,0,10,{ travel: { route, period: 10, offset: index * 1.1 } }));
    });
    return curves;
}

export function arcGeometry() {
    const curves = [];
    for (let panel = 0; panel < 2; panel++) {
        const left = -415 + panel * 455, top = -172, step = 47;
        // A permanent grid keeps the composition full during arrivals.
        for (let layer = 0; layer < 3; layer++) {
            const z = 16 + layer * 13;
            for (let i = 0; i <= 7; i++) {
                curves.push(line([[left+i*step,top,z],[left+i*step,top+7*step,z]]));
                curves.push(line([[left,top+i*step,z],[left+7*step,top+i*step,z]]));
            }
        }
        for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
            const order = panel === 0 ? y*7+x : (x*19+y*31)%49;
            const extra = { arrival: order * .055 + panel * .3 };
            const xx = left+x*step+3, yy = top+y*step+3;
            // Every cell face settles at z=0, with identical thickness.
            curves.push(...box(xx,yy,41,41,0,12,extra));
            curves.push(line(rectangle(xx+5,yy+5,31,31,0),extra));
            curves.push(line([[xx+20.5,yy+5,0],[xx+20.5,yy+36,0]],extra));
            curves.push(line([[xx+5,yy+20.5,0],[xx+36,yy+20.5,0]],extra));
        }
    }
    return curves;
}

export function federatedGeometry() {
    const curves = [];
    for (let i = 0; i < 8; i++) {
        const y = -215 + i*58;
        curves.push(...box(-470,y,28,42,0,9));
        for (let layer=0;layer<3;layer++) curves.push(...box(-365,y+4,58,36,layer*12,0));
        for (let col=1;col<4;col++) curves.push(line([[-365+col*14.5,y+4,24],[-365+col*14.5,y+40,24]]));
        const route=[[-300,y+22,24],[-210,y+22,35],[-110,0,60]];
        curves.push(line(route));
        for(let packet=0;packet<2;packet++) {
            curves.push(...box(-9,-9,18,18,0,12,{travel:{route,period:9,offset:i*.75+packet*4.5}}));
        }
    }
    for (let layer=0;layer<4;layer++) curves.push(...box(-110,-100,160,200,layer*20,0));
    for(let col=1;col<8;col++) curves.push(line([[-110+col*20,-100,60],[-110+col*20,100,60]]));
    for(let row=1;row<10;row++) curves.push(line([[-110,-100+row*20,60],[50,-100+row*20,60]]));
    for(let i=0;i<3;i++) {
        const y=-145+i*115;
        const route=[[50,0,60],[115,0,30],[155,y+38,20]];
        curves.push(line(route),...box(155,y,145,76,0,26));
        curves.push(...box(-12,-8,24,16,0,12,{travel:{route,period:6,offset:i*1.5}}));
        for(let col=1;col<5;col++) curves.push(line([[155+col*29,y,0],[155+col*29,y+76,0]]));
        for(let row=1;row<3;row++) curves.push(line([[155,y+row*25,0],[300,y+row*25,0]]));
        curves.push(line([[300,y+38,0],[365,y+38,0],[410,0,0]]));
    }
    return curves;
}

// Analytic damped spring: starts behind, overshoots toward the viewer, then rests.
// Positive z is farther from the camera. The shared final face is always z=0.
export function displacement(curve, seconds, settled = false) {
    if (curve.arrival !== undefined) {
        if (settled) return [0,0,0];
        const age = seconds % 17 - curve.arrival;
        if (age < 0) return null;
        const z = age >= 3 ? 0 : 280*Math.exp(-2.7*age)*(Math.cos(5.8*age)+.4655*Math.sin(5.8*age));
        return [0,0,z];
    }
    if (curve.travel) {
        const {route,period,offset} = curve.travel;
        const progress = ((seconds+offset)%period)/period*(route.length-1);
        const index = Math.min(route.length-2,Math.floor(progress));
        const fraction = progress-index;
        return route[index].map((value,axis)=>value+(route[index+1][axis]-value)*fraction);
    }
    return [0,0,0];
}

export function projector({ time, phase=0, scale=1, x=600, y=250, depth=1 }) {
    const yaw = Math.sin(time*.1+phase)*.3;
    const pitch = -.12+Math.cos(time*.075+phase)*.13;
    const roll = Math.sin(time*.06+phase)*.055;
    const cy=Math.cos(yaw), sy=Math.sin(yaw), cx=Math.cos(pitch), sx=Math.sin(pitch);
    const cr=Math.cos(roll), sr=Math.sin(roll);
    const driftX=Math.sin(time*.085+phase)*24, driftY=Math.cos(time*.09+phase)*15;
    return ([px,py,pz]) => {
        const z=pz*depth;
        const xx=px*cy+z*sy, zz=-px*sy+z*cy;
        const yy=py*cx-zz*sx, z2=py*sx+zz*cx;
        const perspective=1500/(1500+z2);
        return [x+(xx*cr-yy*sr+driftX)*scale*perspective,y+(xx*sr+yy*cr+driftY)*scale*perspective];
    };
}

// Geometry is immutable after construction. Compile its topology once, then
// project each shared vertex once per motion group, preserving curve order.
// Weak keys let discarded studies and their compiled geometry be collected.
const compiledGeometry = new WeakMap();

function compileGeometry(curves) {
    const groups = new Map(), rows = [];
    for (const curve of curves) {
        const key = curve.arrival !== undefined ? curve.arrival : curve.travel || 'static';
        if (!groups.has(key)) groups.set(key, {
            curve, lookup: new Map(), points: [], output: [], active: false,
        });
        const group = groups.get(key);
        const indices = curve.points.map(point => {
            const vertex = point.join(',');
            if (!group.lookup.has(vertex)) {
                group.lookup.set(vertex, group.points.length);
                group.points.push(point);
            }
            return group.lookup.get(vertex);
        });
        rows.push({ group, indices });
    }
    const scene = { groups: [...groups.values()], rows };
    for (const group of scene.groups) delete group.lookup;
    compiledGeometry.set(curves, scene);
    return scene;
}

export function renderCurves(curves, project, seconds, settled=false) {
    const scene = compiledGeometry.get(curves) || compileGeometry(curves);
    for (const group of scene.groups) {
        const offset = displacement(group.curve, seconds, settled);
        group.active = !!offset;
        if (!offset) continue;
        for (let i = 0; i < group.points.length; i++) {
            const p = group.points[i];
            const [x, y] = project([p[0]+offset[0], p[1]+offset[1], p[2]+offset[2]]);
            group.output[i] = `${x.toFixed(1)},${y.toFixed(1)}`;
        }
    }
    let path = '';
    for (const { group, indices } of scene.rows) {
        if (!group.active) continue;
        for (let i = 0; i < indices.length; i++) {
            path += (i ? 'L' : 'M') + group.output[indices[i]];
        }
    }
    return path;
}
